// frontend/src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import BiometricGate from './components/Auth/BiometricGate';
import { PlaidProvider } from './context/PlaidContext';
import { FetchProvider } from './context/FetchContext';
import { FamilyProvider } from './context/FamilyContext'; // Add this import
import { SharingModeProvider } from './context/SharingModeContext';
import { DarkModeProvider } from './context/DarkModeContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import LandingPage from './components/Landing/LandingPage';
import './styles/tailwind.css';

// Route-level code splitting. The public landing page ("/") is the paid-ads
// entry point and stays in the main bundle so its hero paints as soon as the
// shell loads; everything else (the authenticated app, recharts, dnd-kit,
// markdown, auth screens, blog) is fetched on demand when its route is hit.
//
// Each lazy screen carries its own <Suspense> boundary instead of one boundary
// around the whole <Switch>: "/" is served pre-rendered (build/landing.html)
// and hydrated with the legacy ReactDOM.hydrate API, which cannot hydrate
// through a Suspense boundary. Keeping Suspense off the landing route lets the
// server-rendered hero survive hydration (and stay the LCP element).
const lazyRoute = (loader) => {
  const Lazy = React.lazy(loader);
  const Wrapped = (props) => (
    <React.Suspense fallback={<div className="min-h-screen" />}>
      <Lazy {...props} />
    </React.Suspense>
  );
  return Wrapped;
};
const Dashboard = lazyRoute(() => import('./components/Dashboard/Dashboard'));
const Login = lazyRoute(() => import('./components/Auth/Login'));
const Register = lazyRoute(() => import('./components/Auth/Register'));
const ForgotPassword = lazyRoute(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazyRoute(() => import('./components/Auth/ResetPassword'));
const AdminDashboard = lazyRoute(() => import('./components/Admin/AdminDashboard'));
const OnboardingContainer = lazyRoute(() => import('./components/Onboarding/OnboardingContainer'));
const CalendarCallback = lazyRoute(() => import('./components/Calendar/CalendarCallback'));
const AcceptInvitationPage = lazyRoute(() => import('./components/Family/AcceptInvitationPage'));
const DataFetcher = lazyRoute(() => import('./components/DataFetcher/DataFetcher'));
const WhatIsPage = lazyRoute(() => import('./components/Landing/WhatIsPage'));
const HowDifferentPage = lazyRoute(() => import('./components/Landing/HowDifferentPage'));
const WhoItsForPage = lazyRoute(() => import('./components/Landing/WhoItsForPage'));
const CalculatorsPage = lazyRoute(() => import('./components/Landing/CalculatorsPage'));
const BlogIndex = lazyRoute(() => import('./components/Blog/BlogIndex'));
const BlogPost = lazyRoute(() => import('./components/Blog/BlogPost'));

// Dev-only layout preview (code-split; only mounted in development)
const RecentTransactionsPreview = process.env.NODE_ENV === 'development'
  ? lazyRoute(() => import('./components/Dev/RecentTransactionsPreview'))
  : null;
const RecurringLinksPreview = process.env.NODE_ENV === 'development'
  ? lazyRoute(() => import('./components/Dev/RecentTransactionsPreview').then((m) => ({ default: m.RecurringLinksPreview })))
  : null;

const App = () => {
  return (
    <HelmetProvider>
    <DarkModeProvider>
    <AuthProvider>
      <SharingModeProvider>
        <PlaidProvider>
          <FetchProvider>
              <FamilyProvider>
              <Router>
                <BiometricGate>
                      <Switch>
                        <Route exact path="/login" component={Login} />
                        <Route exact path="/register" component={Register} />
                        <Route exact path="/forgot-password" component={ForgotPassword} />
                        <Route exact path="/reset-password/:token" component={ResetPassword} />
                        {/* Onboarding flow for new and existing users */}
                        <PrivateRoute exact path="/onboarding" component={OnboardingContainer} />
                        <PrivateRoute
                          exact
                          path="/dashboard"
                          component={() => (
                            <DataFetcher>
                              <Dashboard/>
                            </DataFetcher>
                          )}
                        />
                        <Route exact path="/family/invite/:token" component={AcceptInvitationPage} />
                        <PrivateRoute exact path="/calendar-callback" component={CalendarCallback} />
                        <AdminRoute exact path="/admin" component={AdminDashboard} />
                        <Route exact path="/privacy-policy" component={() => {
                          window.location.href = '/privacy-policy.html';
                          return null;
                        }} />
                        <Route exact path="/terms-of-service" component={() => {
                          window.location.href = '/terms-of-service.html';
                          return null;
                        }} />
                        <Route exact path="/data-processing-agreement" component={() => {
                          window.location.href = '/data-processing-agreement.html';
                          return null;
                        }} />
                        {/* Public landing page for marketing */}
                        <Route exact path="/" component={LandingPage} />
                        <Route exact path="/what-is" component={WhatIsPage} />
                        <Route exact path="/how-different" component={HowDifferentPage} />
                        <Route exact path="/who-its-for" component={WhoItsForPage} />
                        <Route exact path="/calculators" component={CalculatorsPage} />
                        <Route exact path="/blog" component={BlogIndex} />
                        <Route exact path="/blog/:slug" component={BlogPost} />
                        {RecentTransactionsPreview && (
                          <Route exact path="/dev/recent-transactions" component={RecentTransactionsPreview} />
                        )}
                        {RecurringLinksPreview && (
                          <Route exact path="/dev/recurring-links" component={RecurringLinksPreview} />
                        )}
                      </Switch>
                </BiometricGate>
                  </Router>
              </FamilyProvider>
          </FetchProvider>
        </PlaidProvider>
      </SharingModeProvider>
    </AuthProvider>
    </DarkModeProvider>
    </HelmetProvider>
  );
};

export default App;